import { ColumnDef, Row } from '@tanstack/react-table';
import { VirtualItem } from '@tanstack/react-virtual';

import { useEmbeddedTableRows } from '../hooks/useEmbeddedTableRows/useEmbeddedTableRows';
import { useVisibleRows } from '../hooks/useVisibleRows/useVisibleRows';
import { DataRenderModes } from '../types';
import { MemoizedTableRow, TableRow } from './TableRow/TableRow';

type VirtualizationConfig = {
  before: number;
  after: number;
  virtualRows: Array<VirtualItem>;
};
type TableBodyProps<TData, TValue> = {
  dataRenderMode: DataRenderModes;
  virtualizationConfig: VirtualizationConfig;
  columns: Array<ColumnDef<TData, TValue>>;
  showCellBorders: boolean;
  columnsWidth: string;
  rows: Array<Row<TData>>;
  parentColumnPaths: Array<string>;
  enableEmbeddedRowActions: boolean;
  isGroupCollapsed?: (rowIndex: number, parentColumnPath: string) => boolean;
  /** Skip re-rendering the rows that did not change. See `DataTableProps.memoizeRows`. */
  memoizeRows?: boolean;
  /** The table's `meta`; a change in it re-renders memoized rows. */
  tableMeta?: unknown;
};

export function TableBody<TData, TValue>({
  columnsWidth,
  showCellBorders,
  dataRenderMode,
  virtualizationConfig,
  columns,
  rows,
  parentColumnPaths,
  enableEmbeddedRowActions,
  isGroupCollapsed,
  memoizeRows = false,
  tableMeta,
}: TableBodyProps<TData, TValue>) {
  const Row = memoizeRows ? MemoizedTableRow : TableRow;
  const { before, after, virtualRows } = virtualizationConfig;
  const colSpan = columns.length;

  const visibleRows = useVisibleRows({
    rows,
    virtualRows,
    isVirtualized: dataRenderMode === DataRenderModes.virtualized,
  });

  const rowsWithEmbeddedTableRowsInfo = useEmbeddedTableRows({
    rows: visibleRows,
    columns,
    parentColumnPaths,
    enableEmbeddedRowActions,
    isGroupCollapsed,
  });

  return (
    <tbody>
      {dataRenderMode === DataRenderModes.virtualized ? (
        <>
          {before > 0 && (
            <tr>
              <td colSpan={colSpan} style={{ height: before }} />
            </tr>
          )}
          {rowsWithEmbeddedTableRowsInfo.map((rowWithEmbeddedTableRowInfo, index) => {
            return (
              <Row
                key={`${rowWithEmbeddedTableRowInfo.row.id}-${rowWithEmbeddedTableRowInfo.embeddedTableRowIndex}`}
                row={rowWithEmbeddedTableRowInfo.row}
                rowIndex={index}
                parentRowIndex={rowWithEmbeddedTableRowInfo.row.index}
                totalRows={rowsWithEmbeddedTableRowsInfo.length}
                showCellBorders={showCellBorders}
                columnsWidth={columnsWidth}
                rowWithEmbeddedTableRowInfo={rowWithEmbeddedTableRowInfo}
                memoMeta={tableMeta}
              />
            );
          })}
          {after > 0 && (
            <tr>
              <td colSpan={colSpan} style={{ height: after }} />
            </tr>
          )}
        </>
      ) : (
        rowsWithEmbeddedTableRowsInfo.map((rowWithEmbeddedTableRowInfo, index) => (
          <Row
            key={`${rowWithEmbeddedTableRowInfo.row.id}-${rowWithEmbeddedTableRowInfo.embeddedTableRowIndex}`}
            row={rowWithEmbeddedTableRowInfo.row}
            rowIndex={index}
            parentRowIndex={rowWithEmbeddedTableRowInfo.row.index}
            totalRows={rowsWithEmbeddedTableRowsInfo.length}
            showCellBorders={showCellBorders}
            columnsWidth={columnsWidth}
            rowWithEmbeddedTableRowInfo={rowWithEmbeddedTableRowInfo}
            memoMeta={tableMeta}
          />
        ))
      )}
    </tbody>
  );
}
